import type { Channel } from "./types";
import { DAY_MS } from "./window";

/**
 * TA-414. The two ways the shop can answer a comment, and Meta's rule for the
 * private one.
 *
 * Kabir ruled merged.md section B TYPED on 2026-09-27:
 *   B1 "not needed unless impossible": no extra gate for public replies.
 *   B2 "wait for owner approval for now": every comment reply is a draft.
 *   B3 "yea": never hide or delete a comment, flag it to the owner.
 *   B4 "yes but keep it simple": the private-reply deadline gets its own
 *      column (`threads.private_reply_deadline_at`, migration 0029).
 *   B5 "yea reply fast, its a high priority": Meta's rule as TA-283 read it,
 *      and the private reply is the path chosen first.
 *   B6 "ok": no public outage acknowledgement.
 *
 * Meta's rule, read from the official docs on 2026-09-17 (FINDINGS TA-283):
 * a private reply must go within 7 days of the comment, exactly one may be
 * sent, and it opens no messaging window. The one-per-comment half is held by
 * the database (`drafts_one_private_reply_uq`, migration 0029), not here.
 */
export type CommentReplyPath = "public" | "private";

export const COMMENT_CHANNELS = ["instagram_comment", "facebook_comment"] as const satisfies readonly Channel[];

export function isCommentChannel(c: Channel): boolean {
  return (COMMENT_CHANNELS as readonly Channel[]).includes(c);
}

export const PRIVATE_REPLY_WINDOW_MS = 7 * DAY_MS;

/** B3: nothing in this system hides or deletes a comment. */
export const COMMENT_MODERATION = "flag_only" as const;

export function privateReplyDeadline(commentAt: Date): Date {
  return new Date(commentAt.getTime() + PRIVATE_REPLY_WINDOW_MS);
}

function privateWindowOpen(commentAt: Date, now: Date): boolean {
  return now.getTime() <= privateReplyDeadline(commentAt).getTime();
}

/**
 * Which path a new draft for a comment takes. The private reply first, while
 * Meta still allows it (B5), because it carries the answer to the person who
 * asked; a public reply otherwise. The owner approves either (B2).
 */
export function chooseCommentReplyPath(input: {
  readonly commentAt: Date;
  readonly now: Date;
  /** A live private-reply draft already exists for this comment. */
  readonly privateReplyTaken: boolean;
}): CommentReplyPath {
  if (input.privateReplyTaken) return "public";
  return privateWindowOpen(input.commentAt, input.now) ? "private" : "public";
}

export interface CommentReply {
  readonly path: CommentReplyPath;
  readonly commentAt: Date;
}

export type CommentReplyGateResult =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly reason: "private_reply_expired" };

/**
 * Run at send time, after approval: a private reply drafted on day 6 and
 * approved on day 8 is one Meta refuses. A public reply has no clock (B1).
 */
export function evaluateCommentReplyGate(reply: CommentReply, now: Date): CommentReplyGateResult {
  if (reply.path === "private" && !privateWindowOpen(reply.commentAt, now)) {
    return { allowed: false, reason: "private_reply_expired" };
  }
  return { allowed: true };
}

/**
 * TA-1354 (cost list item B, Kabir typed 2026-10-17). Why a comment gets no draft, or that it gets one. Sorted by
 * code before any model is called, because comments arrive in far larger numbers than DMs and most ask nothing.
 * The owner still sees every comment in the inbox; this only decides whether a reply is drafted for it.
 */
export type CommentTriage =
  | { readonly draft: true }
  | { readonly draft: false; readonly reason: "no_words" | "tags_only" | "link" | "one_word" };

/** One-word comments that still ask something of the shop, so they are drafted for. Lower case, no punctuation. */
const ONE_WORD_INTENT: ReadonlySet<string> = new Set([
  "price", "prices", "pricing", "cost", "how", "where", "when", "available", "availability", "dm", "pm", "info",
  "details", "size", "sizes", "interested", "link", "carat", "carats",
]);

export function commentTriage(text: string): CommentTriage {
  if (/https?:\/\/|www\./i.test(text)) return { draft: false, reason: "link" };
  const tokens = text.split(/\s+/).filter((w) => /\p{L}/u.test(w));
  if (tokens.length === 0) return { draft: false, reason: "no_words" };
  const words = tokens.filter((w) => !w.startsWith("@"));
  if (words.length === 0) return { draft: false, reason: "tags_only" };
  if (words.length === 1 && !text.includes("?")) {
    const word = (words[0] ?? "").toLowerCase().replace(/[^\p{L}]/gu, "");
    if (!ONE_WORD_INTENT.has(word)) return { draft: false, reason: "one_word" };
  }
  return { draft: true };
}
